 import React from "react";
	 import {
   ResponsiveContainer,
   BarChart,
   Bar,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
 } from "recharts";
 import dayjs from "dayjs";

const convertTimeSpent = (time) => {
  if (!time || time <= 0) return "0m";

  const totalMinutes = Math.floor(time);

  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);

  return parts.join(" ") || "0m";
};

  const ACTIVITY_CONFIG = {
    "Batch Activity": [
      { key: "material", label: "Material", color: "#6CE9A6" },
      { key: "assignment", label: "Assignment", color: "#344BFD" },
      { key: "assessment", label: "Assessment", color: "#F68D2B" },
    ],
    "Other Activity": [
      { key: "mock_tests", label: "Mock Test", color: "#6CE9A6" },
      { key: "coding_challenge", label: "Coding Challenge", color: "#344BFD" },
      { key: "ai_interview", label: "AI Interview", color: "#F68D2B" },
    ],
  };

 
 // ✅ custom tooltip (same as yours)
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    console.log("label", label);
  
    return (
      <div
        style={{
          background: "#fff",
          padding: "8px 10px",
          borderRadius: 8,
          boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
          fontSize: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{dayjs(label, "YYYY-MM", true).isValid() ? `Month: ${dayjs(label).format("MMM-YYYY")}` : `Day: ${dayjs(label).format("DD/MM/YYYY")}`}</div>
        {payload.map((p) => (
          <div key={p.dataKey} style={{ marginBottom: 2 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: p.color,
                marginRight: 6,
              }}
            />
            {p.name}: {convertTimeSpent(p.value)}
          </div>
        ))}
      </div>
    );
  };
 
 // ✅ Preprocess data to mark which segment is top for that day
  const addTopSegmentInfo = (data, keys) =>
    data.map((item) => {
      let topSegment = null;

      // check from last → first so top stack gets rounded
      [...keys].reverse().forEach((k) => {
        if (!topSegment && (item[k] || 0) > 0) {
          topSegment = k;
        }
      });

      return { ...item, topSegment };
    });

  // Calculate legend totals
  const calculateTotals = (data, keys) =>
    data.reduce((acc, cur) => {
      keys.forEach((k) => {
        acc[k] = (acc[k] || 0) + (cur[k] || 0);
      });
      return acc;
    }, {});

 
 // ✅ Custom shape: only top segment has rounded top
 const TopRoundedBar = (props) => {
   const { x, y, width, height, fill, payload, dataKey } = props;
 
   const isTop = payload?.topSegment === dataKey && height > 0;
 
   if (!isTop) {
     // flat rect for non-top segments
     return <rect x={x} y={y} width={width} height={height} fill={fill} />;
   }
 
   const r = Math.min(width, height) / 2;
 
   const path = `
     M ${x} ${y + r}
     Q ${x} ${y} ${x + r} ${y}
     L ${x + width - r} ${y}
     Q ${x + width} ${y} ${x + width} ${y + r}
     L ${x + width} ${y + height}
     L ${x} ${y + height}
     Z
   `;
 
   return <path d={path} fill={fill} />;
 };
 
 
 
 const UserActivityBarChart = ({ data , type = "Batch Activity", grouping = "month" }) => {
  const activityConfig = ACTIVITY_CONFIG[type] || [];

  const [hiddenKeys, setHiddenKeys] = React.useState(new Set());
  
  // Memoize keys array to prevent recreation
  const keys = React.useMemo(
    () => activityConfig.map((a) => a.key),
    [activityConfig]
  );

  const handleLegendClick = React.useCallback((key) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Filter keys based on visibility for segment rounding logic
  const visibleKeys = React.useMemo(
    () => keys.filter(k => !hiddenKeys.has(k)),
    [keys, hiddenKeys]
  );

  const processedData = React.useMemo(
     () => addTopSegmentInfo(data,visibleKeys),
     [data, visibleKeys]
   );
 
  const totals = React.useMemo(
    () => calculateTotals(data, keys),
    [data, keys]
  );


  const CustomLegend = ({ payload, totals }) => {
   if (!payload || !payload.length) return null;
 
   return (
     <div
       style={{
         display: "flex",
         justifyContent: "center",
         gap: 24,          // ✅ space between legends
         marginTop: 16,
         fontSize: 12,
       }}
     >
       {payload.map((item) => {
         const key = item.dataKey;
         const isHidden = hiddenKeys.has(key);
         const total = totals[key] || 0;
 
         return (
           <div
             key={key}
             onClick={() => handleLegendClick(key)}
             style={{
               display: "flex",
               alignItems: "center",
               gap: 8,
               cursor: "pointer",
               opacity: isHidden ? 0.4 : 1, // Dim when hidden
               transition: "opacity 0.2s"
             }}
           >
             {/* ✅ Circle symbol */}
             <span
               style={{
                 width: 10,
                 height: 10,
                 borderRadius: "50%",
                 backgroundColor: item.color,
                 display: "inline-block",
               }}
             />
 
             {/* ✅ Label + Total */}
             <span style={{ color: "#667085", fontFamily: "inter", fontWeight: 700 }}>
               {item.value}
             </span>
 
             <span style={{ color: "#344054", fontFamily: "inter", fontWeight: 700 }}>
               {convertTimeSpent(total)}
             </span>
           </div>
         );
       })}
     </div>
   );
 };

 
 
   return (
     <div style={{ width: "100%", height: "100%" }}>
       <ResponsiveContainer width="100%" height="100%">
         <BarChart data={processedData} margin={{ right: 40 }}>
           <CartesianGrid vertical={false} stroke="#e0e0e0" />
 
           <XAxis
             dataKey={grouping}
             tickLine={false}
             axisLine={{ stroke: "#e0e0e0" }}
             tickFormatter={(d) => (grouping === "day") ? `${dayjs(d).format("DD")}` : `${dayjs(d).format("MMM")}` } //dayjs(d).format("DD")}
             fontSize={12}
             label={{ value: grouping === "day" ? "Days" : "Months", position: "right", offset: grouping === "day" ? 5: -4, fontSize: "12px", fontFamily: "inter", fontWeight: 700, color: "#767676" }}
             style={{fontSize: "12px", fontFamily: "inter", fontWeight: 700, color: "#767676"}}
           />
           
           <YAxis
             tickLine={false}
             axisLine={{ stroke: "#e0e0e0" }}
             tickFormatter={(v) => {
                if (v >= 1440) {
                  return `${Math.floor(v / 1440)}d`;
                }
                if (v >= 60) {
                  return `${Math.floor(v / 60)}h`;
                }
                return `${v}m`;
             }}
             fontSize={12}
             width={40}
           />
 
           <Tooltip content={<CustomTooltip /> } cursor={false} />
 
           <Legend
             verticalAlign="bottom"
             align="center"
             content={(props) => <CustomLegend {...props} totals={totals} />}
           />


           {activityConfig.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.label}
              stackId="a"
              fill={item.color}
              hide={hiddenKeys.has(item.key)} // ✅ Hide the bar if toggled off
              barSize={12}
              shape={<TopRoundedBar />}
              isAnimationActive={true}
            />
          ))}
         </BarChart>
       </ResponsiveContainer>
     </div>
   );
 };
 
 export default UserActivityBarChart;